/**
 * Any node as its own view (SPEC §3.2): pages, journal days and zoomed blocks. A breadcrumb leads
 * back; a page's title is editable in place (a rename rewrites every ref, D57); a zoomed block is
 * an editable header (⏎ in it adds a first child); its children follow, then the linked
 * references (editable where they are) and, on demand, the unlinked ones.
 */
import { Star } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import {
  isType,
  mergedTarget,
  plainText,
  refsOf,
  TAG,
  titleKey,
  visibleRows,
  type Graph,
  type Node,
  type NodeId,
} from '@solar/core'
import { ghostForNode, type ViewRow } from '../client/store.ts'
import { EntityPanel, entitySections, isEntity } from './entity.tsx'
import { ancestors, hrefOf, label, referenceRows } from './refrows.ts'
import { dayTitle } from './dates.ts'
import { useNode, useStore, useStructure } from './hooks.ts'
import { FieldGrid, PageTags, SchemaEditor } from './fields.tsx'
import { Outline } from './Outline.tsx'
import { navigate, useNavSeq } from './router.ts'

/** Blocks that mention the title as plain text, without linking it. */
function unlinkedIds(g: Graph, node: Node): NodeId[] {
  if (!node.title) return []
  const key = titleKey(node.title)
  const words = new RegExp(`(^|[^\\p{L}\\p{N}_])${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=$|[^\\p{L}\\p{N}_])`, 'iu')
  const out: NodeId[] = []
  for (const n of g.nodes()) {
    if (n.kind !== 'block' || n.deletedAt !== null || n.content === '') continue
    if (!n.content.toLowerCase().includes(key)) continue
    if (refsOf(n.content).some((r) => r.title !== null && titleKey(r.title) === key)) continue
    if (words.test(plainText(n.content))) out.push(n.id)
  }
  return out
}

function TitleEditor({ node }: { node: Node }) {
  const store = useStore()
  const [value, setValue] = useState(node.title ?? '')
  const [error, setError] = useState<string | null>(null)
  const editing = useRef(false)
  useEffect(() => {
    if (!editing.current) setValue(node.title ?? '')
  }, [node.title])
  const commit = (): void => {
    editing.current = false
    const err = store.rename(node.id, value)
    setError(err)
  }
  return (
    <div className="titlebox">
      <input
        className="title"
        value={value}
        aria-label="Title"
        spellCheck={false}
        onFocus={() => {
          editing.current = true
          store.idle()
        }}
        onChange={(e) => {
          setValue(e.target.value)
          setError(null)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
            if (store.titles.resolve(value) === node.id) e.currentTarget.blur()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            editing.current = false
            setValue(node.title ?? '')
            setError(null)
            e.currentTarget.blur()
          }
        }}
        onBlur={() => {
          if (editing.current) commit()
        }}
      />
      {error && (
        <div className="title-error" role="alert">
          {error}
        </div>
      )}
    </div>
  )
}

/** Title, explicit tags, fields and (for tags) the schema editor: the first row of a page. */
function PageHead({ node }: { node: Node }) {
  const store = useStore()
  const usedAsTag = node.kind === 'tag' || isType(store.g, node.id) || store.refs.taggedWith(node).length > 0
  const fav = node.props['sys:favorite'] === true
  return (
    <div className="pagehead">
      <div className="titlerow">
        <TitleEditor node={node} />
        <button
          className={fav ? 'link star on' : 'link star'}
          aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
          aria-pressed={fav}
          title={fav ? 'In favorites (left sidebar)' : 'Add to favorites'}
          onClick={() => store.setField(node.id, 'sys:favorite', fav ? null : true)}
        >
          <Star size={16} fill={fav ? 'currentColor' : 'none'} />
        </button>
      </div>
      {node.kind !== 'tag' && <PageTags node={node} />}
      <FieldGrid node={node} active full />
      {usedAsTag && <SchemaEditor node={node} />}
    </div>
  )
}

export function NodeView({ id, block = null, viewKey = 'main' }: { id: NodeId; block?: string | null; viewKey?: string }) {
  const store = useStore()
  const navSeq = useNavSeq()
  const highlight = useMemo(() => (block ? { id: block, seq: navSeq } : null), [block, navSeq])
  const version = useStructure()
  const refsVersion = useSyncExternalStore(store.refs.subscribe, store.refs.getVersion, store.refs.getVersion)
  const titlesVersion = useSyncExternalStore(store.titles.subscribe, store.titles.getVersion, store.titles.getVersion)
  const tasksVersion = useSyncExternalStore(store.tasks.subscribe, store.tasks.getVersion, store.tasks.getVersion)
  const schemasVersion = useSyncExternalStore(store.subscribeSchemas, store.getSchemasVersion, store.getSchemasVersion)
  const node = useNode(id)
  const [showUnlinked, setShowUnlinked] = useState(false)

  const rows = useMemo(() => {
    const g = store.g
    const out: ViewRow[] = []
    if (!g.isLive(id)) return out
    const n = g.must(id)
    if (n.kind === 'page' || n.kind === 'tag') out.push({ type: 'extra', key: 'pagehead', what: 'pagehead', id })
    if (isEntity(g, n)) out.push({ type: 'extra', key: 'entity', what: 'entity', id })
    if (n.kind === 'block') out.push({ type: 'block', key: `header:${id}`, id, depth: -1, root: id })
    for (const r of visibleRows(g, id)) out.push({ type: 'block', key: r.id, id: r.id, depth: r.depth, root: id })
    if (out.filter((r) => r.type === 'block' && r.depth >= 0).length === 0) {
      out.push({ type: 'ghost', key: ghostForNode(id), id: ghostForNode(id), root: id, date: null })
    }
    // A person or company lists its open tasks and meetings (timelines by day).
    const entity = isEntity(g, n) ? entitySections(store, n) : null
    if (entity) out.push(...entity.rows)
    // A type lists its instances (#x in text and explicit tags); #task lists every open task.
    const instances = new Set<NodeId>()
    let closed = 0
    if (isType(g, id)) {
      const taskTag = mergedTarget(g, TAG.task) === id
      for (const x of taskTag ? store.tasks.all().map((t) => t.id) : store.refs.taggedWith(n)) {
        const status = taskTag ? store.tasks.statusOf(g.must(x)) : null
        if (status === 'done' || status === 'cancelled') closed++
        else instances.add(x)
      }
      const inst = referenceRows(store, n, [...instances], 'ins:')
      if (inst.count > 0 || closed > 0) {
        const what = taskTag ? 'open task' : 'instance'
        out.push({ type: 'label', key: 'instances', level: 'section', text: `${inst.count} ${what}${inst.count === 1 ? '' : 's'}${closed ? ` · ${closed} done` : ''}` })
        out.push(...inst.rows)
      }
    }
    const linked = referenceRows(
      store,
      n,
      store.refs.referrersOf(n).filter((x) => !instances.has(x) && !entity?.shown.has(x)),
      'ref:',
    )
    if (linked.count > 0) {
      out.push({ type: 'label', key: 'refs', level: 'section', text: `${linked.count} linked reference${linked.count === 1 ? '' : 's'}` })
      out.push(...linked.rows)
    }
    if (n.title) {
      const unlinked = showUnlinked ? referenceRows(store, n, unlinkedIds(g, n), 'unl:') : null
      out.push({
        type: 'label',
        key: 'unlinked',
        level: 'section',
        text: unlinked ? `${unlinked.count} unlinked reference${unlinked.count === 1 ? '' : 's'}` : 'Unlinked references',
        open: showUnlinked,
        onToggle: () => setShowUnlinked((v) => !v),
      })
      if (unlinked) out.push(...unlinked.rows)
    }
    return out
  }, [store, version, refsVersion, titlesVersion, tasksVersion, schemasVersion, id, showUnlinked]) // eslint-disable-line react-hooks/exhaustive-deps

  // A duplicate page merged into another (D58): show the one that stayed.
  useEffect(() => {
    if (viewKey !== 'main') return
    if (node && node.deletedAt !== null) {
      const target = mergedTarget(store.g, id)
      if (target !== id) {
        history.replaceState(null, '', `/n/${target}`)
        dispatchEvent(new PopStateEvent('popstate'))
      }
    }
  }, [node, id, store, viewKey])

  // Esc with nothing selected leaves the zoom (SPEC §6.3), in the main view only.
  useEffect(() => {
    if (viewKey !== 'main') return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || store.mode.kind !== 'idle' || e.defaultPrevented) return
      if (e.target instanceof HTMLInputElement) return
      const n = store.g.get(id)
      const parent = n?.parentId ? store.g.get(n.parentId) : undefined
      if (parent) navigate(hrefOf(parent))
      else if (n?.kind === 'block' || n?.kind === 'page') navigate('/')
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [store, id, viewKey])

  if (!node || node.deletedAt !== null) {
    return (
      <div className="notice">
        This node does not exist (or was deleted). <a href="/" onClick={(e) => (e.preventDefault(), navigate('/'))}>Back to the journal</a>
      </div>
    )
  }
  const trail = ancestors(store.g, id)
  return (
    <div className="nodeview">
      <nav className="crumbs">
        {trail.map((a) => (
          <span key={a.id}>
            <a href={hrefOf(a)} onClick={(e) => (e.preventDefault(), navigate(hrefOf(a)))}>
              {label(a)}
            </a>
            <span className="sep"> › </span>
          </span>
        ))}
      </nav>
      {node.kind === 'journal' && <h1 className="title">{label(node)}</h1>}
      <Outline
        rows={rows}
        viewKey={viewKey}
        highlight={highlight}
        renderExtra={(r) =>
          r.what === 'pagehead' ? <PageHead node={store.g.must(r.id)} /> : r.what === 'entity' ? <EntityPanel node={store.g.must(r.id)} /> : null
        }
      />
    </div>
  )
}
